import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Loader2 } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import QuoteForm from "../components/QuoteForm";
import { BUSINESS, canonical } from "../data/business";
import { IMG } from "../data/images";
import { faqs } from "../data/faqs";
import FaqAccordion from "../components/FaqAccordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill all required fields");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll reply within 24 hours");
      setForm({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
    } catch { toast.error("Could not send — please call us directly"); }
    finally { setLoading(false); }
  };

  return (
    <div className="page-enter">
      <Seo title="Contact Last Cut Landscaping — Free Quotes Scotland" description="Get in touch with Last Cut Landscaping. Free quotes across Edinburgh, Glasgow, Falkirk & the Central Belt of Scotland." canonical={canonical("/contact")} />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "Contact" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">Get in touch</h1>
        <p className="text-dark/70 text-lg max-w-2xl mt-4">Free quotes, honest advice, and a team that turns up when we say we will.</p>

        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          <div className="lg:col-span-5 space-y-4">
            <ContactRow icon={<Phone/>} title="Call us" v={BUSINESS.phone} href={`tel:${BUSINESS.phoneDial}`} />
            <ContactRow icon={<MessageCircle/>} title="WhatsApp" v={`+${BUSINESS.whatsapp}`} href={`https://wa.me/${BUSINESS.whatsapp}`} />
            <ContactRow icon={<Mail/>} title="Email" v={BUSINESS.email} href={`mailto:${BUSINESS.email}`} />
            <ContactRow icon={<MapPin/>} title="Service area" v={BUSINESS.hq} />
            <div className="rounded-2xl overflow-hidden aspect-[4/3] mt-6"><img src={IMG.largeDeckTurf} alt="Recent Last Cut Landscaping project in the Central Belt of Scotland" loading="lazy" className="w-full h-full object-cover"/></div>
          </div>
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="lc-card p-6 lg:p-8">
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={form.name} onChange={set("name")} placeholder="Full name *" data-testid="contact-name" className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30" />
                <input value={form.phone} onChange={set("phone")} placeholder="Phone" type="tel" data-testid="contact-phone" className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30" />
                <input value={form.email} onChange={set("email")} placeholder="Email *" type="email" data-testid="contact-email" className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30" />
                <input value={form.subject} onChange={set("subject")} placeholder="Subject" data-testid="contact-subject" className="px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30" />
              </div>
              <textarea value={form.message} onChange={set("message")} placeholder="How can we help? *" rows={5} data-testid="contact-message" className="mt-3 w-full px-4 py-3 rounded-xl border border-lc bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dark/30" />
              <button type="submit" disabled={loading} className="btn-primary mt-4" data-testid="contact-submit">{loading ? <Loader2 className="animate-spin" size={16}/> : null} {loading ? "Sending…" : "Send message"}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

const ContactRow = ({ icon, title, v, href }) => (
  <a href={href} className="flex items-center gap-4 p-5 bg-paper rounded-2xl hover:bg-dark hover:text-white transition group">
    <div className="w-11 h-11 rounded-xl bg-white grid place-items-center text-dark group-hover:bg-lime">{icon}</div>
    <div><div className="text-xs uppercase tracking-wider opacity-70">{title}</div><div className="font-semibold">{v}</div></div>
  </a>
);

export function QuotePage() {
  return (
    <div className="page-enter">
      <Seo title="Free Landscaping Quote Scotland — Last Cut" description="Get a free, no-obligation landscaping quote anywhere in the Central Belt of Scotland." canonical={canonical("/quote")} />
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "Free Quote" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">Free, no-obligation quote</h1>
        <p className="text-dark/70 text-lg max-w-2xl mt-4">Tell us about your project. We'll respond within 24 hours, usually sooner.</p>
        <div className="mt-10"><QuoteForm sourcePage="/quote" /></div>
      </section>
    </div>
  );
}

export function AboutPage() {
  return (
    <div className="page-enter">
      <Seo title="About Last Cut Landscaping — Scotland's Premium Landscapers" description="Meet Last Cut Landscaping — Scotland's premium landscaping team serving the Central Belt." canonical={canonical("/about")} />
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "About" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">A Scottish landscaping team you can <span className="italic text-[color:var(--lc-green)]">actually</span> trust.</h1>
        <p className="text-dark/75 text-lg mt-6 leading-relaxed">Last Cut Landscaping is a Central Belt landscaping team that lives and works in Scotland. From the first phone call through to the final tidy-up, the people you speak to are the people on site — no salespeople, no middle-men, no subcontracted dropoffs.</p>
        <p className="text-dark/75 text-lg mt-4 leading-relaxed">What we promise: we still answer our own phone, still turn up when we say we will, and still finish every project the way we'd finish our own garden. <strong>Quality care. Lasting impressions.</strong> That's the promise on our logo and the rule we measure every job against.</p>
        <div className="rounded-3xl overflow-hidden aspect-[16/9] mt-10"><img src={IMG.largeDeckTurf} alt="Last Cut Landscaping recent project" className="w-full h-full object-cover"/></div>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {[{n:"£5m",l:"Public liability insurance"},{n:"2-yr",l:"Workmanship guarantee"},{n:"100%",l:"Authentic project photos"}].map((c,i)=>(
            <div key={i} className="lc-card p-6"><div className="font-display text-5xl text-dark">{c.n}</div><div className="text-dark/65 mt-1 text-sm">{c.l}</div></div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <div className="page-enter">
      <Seo title="Privacy Policy — Last Cut Landscaping" description="How Last Cut Landscaping collects, uses and protects your personal data. GDPR compliant privacy policy." canonical={canonical("/privacy")} />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "Privacy" }]} />
        <h1 className="text-display font-display text-5xl lg:text-6xl text-dark mt-6">Privacy Policy</h1>
        <div className="prose-blog mt-8">
          <p>Last updated: {new Date().toLocaleDateString("en-GB")}. This policy explains what data we collect when you use lastcutlandscaping.co.uk or request a quote.</p>
          <h2>What we collect</h2>
          <p>When you submit a quote or contact form we collect: your name, email, phone number, postcode (optional), service interest and the message you send. We collect basic anonymised analytics about page views.</p>
          <h2>How we use it</h2>
          <p>Solely to respond to your enquiry and to provide the requested service. We never sell or share your data with third parties for marketing.</p>
          <h2>Data retention</h2>
          <p>Quote and contact records are kept for 3 years from your last interaction. You can request deletion at any time by emailing {BUSINESS.email}.</p>
          <h2>Cookies</h2>
          <p>We use essential cookies only. No tracking or advertising cookies are set without your consent.</p>
          <h2>Your rights (GDPR)</h2>
          <p>You have the right to access, correct, delete or export your personal data. Email {BUSINESS.email} to exercise any of these rights.</p>
          <h2>Contact</h2>
          <p>Last Cut Landscaping · {BUSINESS.hq} · {BUSINESS.email}</p>
        </div>
      </section>
    </div>
  );
}

export function TermsPage() {
  return (
    <div className="page-enter">
      <Seo title="Terms of Service — Last Cut Landscaping" description="Terms and conditions for using Last Cut Landscaping services and website." canonical={canonical("/terms")} />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "Terms" }]} />
        <h1 className="text-display font-display text-5xl lg:text-6xl text-dark mt-6">Terms of Service</h1>
        <div className="prose-blog mt-8">
          <p>Last updated: {new Date().toLocaleDateString("en-GB")}.</p>
          <h2>1. Quotes</h2>
          <p>All quotes are free and valid for 30 days from the date of issue. Quoted prices include materials and labour unless explicitly stated otherwise.</p>
          <h2>2. Acceptance</h2>
          <p>A booking is confirmed once you sign and return the quote, or pay the deposit. A 25% deposit is typically required for projects over £2,000.</p>
          <h2>3. Workmanship guarantee</h2>
          <p>All our landscaping installs come with a 2-year workmanship guarantee. Material warranties (10–15 years on artificial grass, composite decking etc.) are passed through from the manufacturer.</p>
          <h2>4. Payment</h2>
          <p>Final balance is due on satisfactory completion of the project, payable by bank transfer or card.</p>
          <h2>5. Cancellation</h2>
          <p>You may cancel within 14 days of accepting a quote with no charge. After this, reasonable costs already incurred may be deducted.</p>
          <h2>6. Liability</h2>
          <p>We carry £5,000,000 public liability insurance. Our liability is limited to the value of the contracted works.</p>
          <h2>7. Disputes</h2>
          <p>Any disputes are governed by Scots law and subject to the exclusive jurisdiction of the Scottish courts.</p>
          <h2>Contact</h2>
          <p>{BUSINESS.email} · {BUSINESS.hq}</p>
        </div>
      </section>
    </div>
  );
}

export function ProjectDetailPage() {
  return null; // handled separately in /app/frontend/src/pages/ProjectDetail.jsx
}

export function FaqPage() {
  return (
    <div className="page-enter">
      <Seo title="FAQ — Last Cut Landscaping Scotland" description="Common questions about our landscaping services, areas covered, costs and process." canonical={canonical("/faq")} />
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pt-10 lg:pt-14 pb-20">
        <Breadcrumbs items={[{ label: "FAQ" }]} />
        <h1 className="text-display font-display text-5xl lg:text-7xl text-dark mt-6">FAQs</h1>
        <p className="text-dark/70 text-lg mt-4">Quick answers to common questions.</p>
        <div className="mt-10"><FaqAccordion items={faqs} /></div>
      </section>
    </div>
  );
}
